import type { FC } from "react";
import React from "react";

import cn from "classnames";

import { getChildrenRecord } from "../../utils/react";

import type { MediaProps } from "./types";

import classes from "./Media.styl";

export const MEDIA_DISPLAY_NAME = "Media";

/**
 * Компонент для демонстрации медиа пользователю.
 *
 * Представляет собой контейнер, внутрь которого можно передать любое медиа (например, изображение, видео, гифка),
 * с помощью которого можно передать разработчику точную информацию о соотношении сторон, скруглениях, отступах и
 * наложенных дополнительным слоем аддонах.
 *
 * <a href="https://www.figma.com/design/vTg3KAvmAXA9LigeyiqCWL/%F0%9F%92%A0-Base-Components?m=auto&node-id=6914-7181&t=PELROivWbv1Y1luR-1" target="_blank">Спека</a>
 * <br><br>
 * ### Дочерние компоненты
 *
 * | Компонент | Использование |
 * |-----------|----------|
 * | `<Media.Content />` | Позволяет поместить медиа-файлы внутрь компонента. |
 * | `<UniBoxAdditional />` | Используется вместо аддонов для добавления элементов в различные части компонента. Элементы в слотах UniBoxAdditional могут перекрывать друг друга и выходить за пределы контейнера. <a href="https://uikit.odkl.ru/?path=/docs/компоненты-unibox-uniboxadditional--docs" target="_blank">Спека</a> |
 * | `<UniBoxMain />` | Используется вместо аддонов для добавления элементов в различные части компонента. Элементы в слотах UniBoxMain не перекрывают друг друга, не выходят за пределы слота и контейнера. <a href="https://uikit.odkl.ru/?path=/docs/компоненты-unibox-uniboxmain--docs" target="_blank">Спека</a> |
 */
export const Media: FC<MediaProps> = ({
    children,
    customVarsStyles,
    aspectRatio = { width: 1, height: 1 },
    roundCorners = false,
    "data-tsid": tsid,
}) => {
    const {
        Content,
        TopRightAddon,
        TopLeftAddon,
        BottomLeftAddon,
        BottomRightAddon,
        CenterAddon,
        ...otherElements
    } = getChildrenRecord(children, MEDIA_DISPLAY_NAME, {
        Content: {
            required: true,
        },
        Unknown: {
            skipElement: false,
        },
    });

    const isAspectRationAuto = aspectRatio === "auto";

    const ratioStyle = !isAspectRationAuto
        ? {
              paddingBottom: `${100 / (aspectRatio.width / aspectRatio.height)}%`,
          }
        : undefined;

    const containerClass = cn(
        classes["container"],
        {
            [classes["__round-corners"]]: roundCorners,
            [classes["__aspect_ratio_auto"]]: isAspectRationAuto,
        },
        customVarsStyles,
    );

    return (
        <div
            className={containerClass}
            data-tsid={tsid}
            data-uikit={MEDIA_DISPLAY_NAME}
        >
            <div className={classes["media-clipper"]}>
                <div className={classes["ratio-container"]} style={ratioStyle}>
                    <div className={classes["media-wrapper"]}>{Content}</div>
                </div>
            </div>

            {TopLeftAddon}
            {TopRightAddon}
            {BottomLeftAddon}
            {BottomRightAddon}
            {CenterAddon}
            {Object.values(otherElements)}
        </div>
    );
};

Media.displayName = MEDIA_DISPLAY_NAME;

export default Media;
